import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { FirebaseError } from "firebase/app";
import {
  browserLocalPersistence,
  createUserWithEmailAndPassword,
  deleteUser,
  EmailAuthProvider,
  getAdditionalUserInfo,
  getIdTokenResult,
  GoogleAuthProvider,
  onAuthStateChanged,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  sendPasswordResetEmail,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  User,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import {
  ArrowLeft,
  Camera,
  CircleCheck,
  ClipboardList,
  Heart,
  KeyRound,
  LockKeyhole,
  LogOut,
  Mail,
  ShieldCheck,
  Trash2,
  UserPlus,
  X,
} from "lucide-react";
import { firebaseConfigured, getFirebaseServices } from "./firebase";
import TerraBoaApp, { AuthIntent } from "./terra-boa-app";
import type { SupplyStore, ViewKey } from "./terra-boa-data";
import { AvatarVisual } from "./terra-boa-profile-avatar";
import { accountAvatar } from "./terra-boa-profile-photos";
import { saveAccountLocation, subscribeLocationCatalog, subscribeSupplyStores, TerraBoaSession } from "./terra-boa-repository";
import { LoadingSponsors } from "./terra-boa-loading-sponsors";
import { DeferredLocationFields as LocationFields } from "./terra-boa-deferred-location-fields";
import { BrazilLocation, catalogCommercialPlaces, catalogMunicipalities, EMPTY_BRAZIL_LOCATION, isValidBrazilLocation, LocationCatalogEntry, locationFromParts } from "./terra-boa-locations";

type AuthView = "login" | "register" | "reset" | "account";
type AuthDialogState = {
  view: AuthView;
  intent: AuthIntent | "account";
};

function guestSession(): TerraBoaSession {
  return { mode: "firebase", profileId: "" };
}

export default function TerraBoaRoot() {
  const [session, setSession] = useState<TerraBoaSession>(
    firebaseConfigured ? guestSession() : { mode: "local", profileId: "" },
  );
  const [authReady, setAuthReady] = useState(!firebaseConfigured);
  const [authMessage, setAuthMessage] = useState("");
  const [authDialog, setAuthDialog] = useState<AuthDialogState | null>(null);
  const [locationCatalog, setLocationCatalog] = useState<LocationCatalogEntry[]>([]);
  const [supplyStores, setSupplyStores] = useState<SupplyStore[]>([]);
  const [navigationRequest, setNavigationRequest] = useState<{ id: number; view: ViewKey } | null>(null);
  const [favoritesRequest, setFavoritesRequest] = useState<number | null>(null);
  const [profileSetupRequest, setProfileSetupRequest] = useState<number | null>(null);
  const [profilePhotoRequest, setProfilePhotoRequest] = useState<number | null>(null);
  const [accountPhotoUrl, setAccountPhotoUrl] = useState<string | undefined>();
  const registrationPending = useRef(false);
  const additionalMunicipalities = useMemo(() => catalogMunicipalities(locationCatalog), [locationCatalog]);
  const additionalPlaces = useMemo(() => catalogCommercialPlaces(locationCatalog), [locationCatalog]);

  async function resolveUser(user: User) {
    const services = getFirebaseServices();
    if (!services) throw new Error("firebase-unavailable");
    const [control, preferences] = await Promise.all([
      getDoc(doc(services.db, "accountControls", user.uid)),
      getDoc(doc(services.db, "accountPreferences", user.uid)),
    ]);
    if (control.exists() && control.data().status === "blocked") throw new Error("account-blocked");
    const token = await getIdTokenResult(user, true);
    const location = preferences.exists() ? locationFromParts(preferences.data()) : EMPTY_BRAZIL_LOCATION;
    setSession({
      mode: "firebase",
      profileId: typeof token.claims.profileId === "string" ? token.claims.profileId : "",
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      providerId: user.providerData[0]?.providerId ?? null,
      isAdmin: token.claims.admin === true,
      ...(location.state && location.municipality && location.municipalityCode ? location : {}),
    });
    setAuthMessage("");
    setAuthDialog(null);
  }

  useEffect(() => {
    const services = getFirebaseServices();
    if (!services) return;

    return onAuthStateChanged(services.auth, async (user) => {
      if (!user) {
        setSession(guestSession());
        if (!registrationPending.current) setAuthDialog(null);
        setAuthReady(true);
        return;
      }

      if (registrationPending.current) {
        setAuthReady(true);
        return;
      }

      try {
        await resolveUser(user);
      } catch (error) {
        setSession(guestSession());
        setAuthMessage(error instanceof Error && error.message === "account-blocked"
          ? "Esta conta está bloqueada. Entre em contato com o suporte Terra Boa."
          : "Não foi possível validar sua conta. Tente entrar novamente.");
        await signOut(services.auth);
      } finally {
        setAuthReady(true);
      }
    });
  }, []);

  useEffect(() => subscribeLocationCatalog(session, setLocationCatalog, () => undefined), [session.mode]);
  useEffect(() => subscribeSupplyStores(session, setSupplyStores, () => undefined), [session.mode]);

  function openAuth(intent: AuthIntent) {
    setAuthDialog({ view: "login", intent });
  }

  async function completeRegistration(user: User) {
    await resolveUser(user);
    setProfileSetupRequest(Date.now());
  }

  if (!authReady) return <LoadingScreen />;

  const firebaseUser = getFirebaseServices()?.auth.currentUser ?? null;
  return (
    <>
      <TerraBoaApp
        session={session}
        locationCatalog={locationCatalog}
        supplyStores={supplyStores}
        navigationRequest={navigationRequest}
        favoritesRequest={favoritesRequest}
        profileSetupRequest={profileSetupRequest}
        profilePhotoRequest={profilePhotoRequest}
        onRequestAuth={openAuth}
        onManageAccount={firebaseUser ? (photoUrl) => { setAccountPhotoUrl(photoUrl); setAuthDialog({ view: "account", intent: "account" }); } : undefined}
        onLogout={session.mode === "firebase" && session.uid ? async () => {
          const services = getFirebaseServices();
          if (services) await signOut(services.auth);
        } : undefined}
      />
      {firebaseConfigured && authDialog && (
        <AuthDialog
          state={authDialog}
          initialMessage={authMessage}
          user={firebaseUser}
          accountPhotoUrl={accountPhotoUrl}
          onClose={() => setAuthDialog(null)}
          onOpenMyListings={() => {
            setAuthDialog(null);
            setNavigationRequest({ id: Date.now(), view: "meus-anuncios" });
          }}
          onOpenFavorites={() => {
            setAuthDialog(null);
            setFavoritesRequest(Date.now());
          }}
          onOpenProfileSettings={() => {
            setAuthDialog(null);
            setNavigationRequest({ id: Date.now(), view: "perfil" });
          }}
          onOpenProfilePhoto={() => {
            setAuthDialog(null);
            setProfilePhotoRequest(Date.now());
          }}
          onNavigate={(view) => setAuthDialog((current) => current ? { ...current, view } : null)}
          onRegistered={completeRegistration}
          onRegistrationPending={(pending) => { registrationPending.current = pending; }}
          additionalMunicipalities={additionalMunicipalities}
          additionalPlaces={additionalPlaces}
        />
      )}
    </>
  );
}

function LoadingScreen() {
  return <main className="auth-shell"><section className="auth-card auth-loading"><span className="auth-brand"><img src="/icons/icon-192.png?v=20260905" alt="" /></span><h1>Terra Boa</h1><p>Preparando a plataforma…</p></section><LoadingSponsors /></main>;
}

function AuthDialog({ state, initialMessage, user, accountPhotoUrl, onClose, onOpenMyListings, onOpenFavorites, onOpenProfileSettings, onOpenProfilePhoto, onNavigate, onRegistered, onRegistrationPending, additionalMunicipalities, additionalPlaces }: {
  state: AuthDialogState;
  initialMessage: string;
  user: User | null;
  accountPhotoUrl?: string;
  onClose: () => void;
  onOpenMyListings: () => void;
  onOpenFavorites: () => void;
  onOpenProfileSettings: () => void;
  onOpenProfilePhoto: () => void;
  onNavigate: (view: AuthView) => void;
  onRegistered: (user: User) => Promise<void>;
  onRegistrationPending: (pending: boolean) => void;
  additionalMunicipalities: ReturnType<typeof catalogMunicipalities>;
  additionalPlaces: ReturnType<typeof catalogCommercialPlaces>;
}) {
  return <div className="auth-overlay" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
    <section className="auth-card auth-dialog" role="dialog" aria-modal="true" aria-label={state.view === "account" ? "Gerenciar conta" : "Acessar a Terra Boa"}>
      <button className="auth-modal-close" type="button" onClick={onClose} aria-label="Fechar"><X size={20} /></button>
      <AuthLogo />
      {state.view === "login" && <LoginForm intent={state.intent} initialMessage={initialMessage} onRegister={() => onNavigate("register")} onReset={() => onNavigate("reset")} onRegistered={onRegistered} />}
      {state.view === "register" && <RegisterForm onBack={() => onNavigate("login")} onRegistered={onRegistered} onRegistrationPending={onRegistrationPending} additionalMunicipalities={additionalMunicipalities} additionalPlaces={additionalPlaces} />}
      {state.view === "reset" && <ResetPasswordForm onBack={() => onNavigate("login")} />}
      {state.view === "account" && user && <AccountPanel user={user} photoUrl={accountPhotoUrl} onClose={onClose} onOpenMyListings={onOpenMyListings} onOpenFavorites={onOpenFavorites} onOpenProfileSettings={onOpenProfileSettings} onOpenProfilePhoto={onOpenProfilePhoto} />}
    </section>
  </div>;
}

function AuthLogo() {
  return <div className="auth-logo"><span><img src="/icons/icon-192.png?v=20260905" alt="" /></span><div><strong>Terra Boa</strong><small>Agrointegração</small></div></div>;
}

function intentCopy(intent: AuthIntent | "account") {
  if (intent === "favorite") return "Entre para salvar produtos e demandas na sua lista de favoritos.";
  if (intent === "publish") return "Entre para cadastrar uma publicação na Terra Boa.";
  if (intent === "report") return "Entre para denunciar uma publicação e acompanhar a análise.";
  return "Acesse sua conta ou continue navegando sem login.";
}

function LoginForm({ intent, initialMessage, onRegister, onReset, onRegistered }: {
  intent: AuthIntent | "account";
  initialMessage: string;
  onRegister: () => void;
  onReset: () => void;
  onRegistered: (user: User) => Promise<void>;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState(initialMessage);
  const [busy, setBusy] = useState<"email" | "google" | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const services = getFirebaseServices();
    if (!services || !email.trim() || !password) return;
    setBusy("email");
    setMessage("");
    try {
      services.auth.languageCode = "pt-BR";
      await setPersistence(services.auth, browserLocalPersistence);
      await signInWithEmailAndPassword(services.auth, email.trim(), password);
    } catch (error) {
      setMessage(authErrorMessage(error, "Não foi possível entrar. Confira o e-mail e a senha."));
    } finally {
      setBusy(null);
    }
  }

  async function handleGoogle() {
    const services = getFirebaseServices();
    if (!services) return;
    setBusy("google");
    setMessage("");
    try {
      services.auth.languageCode = "pt-BR";
      await setPersistence(services.auth, browserLocalPersistence);
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: "select_account" });
      const credential = await signInWithPopup(services.auth, provider);
      if (getAdditionalUserInfo(credential)?.isNewUser) await onRegistered(credential.user);
    } catch (error) {
      setMessage(authErrorMessage(error, "Não foi possível entrar com o Google."));
    } finally {
      setBusy(null);
    }
  }

  return <>
    <span className="auth-kicker"><ShieldCheck size={15} />Acesso opcional</span>
    <h1>Entre na Terra Boa</h1>
    <p className="auth-intro">{intentCopy(intent)}</p>
    <button className="auth-google" type="button" onClick={() => void handleGoogle()} disabled={busy !== null}><span className="google-mark">G</span>{busy === "google" ? "Conectando ao Google…" : "Continuar com Google (Gmail)"}</button>
    <div className="auth-divider"><span>ou use e-mail e senha</span></div>
    <form className="auth-login-form" onSubmit={handleSubmit}>
      <label><span>E-mail</span><input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="seuemail@exemplo.com" autoComplete="email" required /></label>
      <label><span>Senha</span><div className="auth-password"><LockKeyhole size={17} /><input type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Digite sua senha" autoComplete="current-password" required /></div></label>
      {message && <div className="auth-error" role="alert">{message}</div>}
      <button className="auth-submit" disabled={busy !== null || !email.trim() || !password}>{busy === "email" ? "Entrando…" : "Entrar com e-mail"}</button>
      <button className="auth-text-button" type="button" onClick={onReset}><KeyRound size={15} />Esqueci minha senha</button>
    </form>
    <div className="auth-account-action"><p><strong>Ainda não possui conta?</strong><small>Crie seu acesso sem confirmação de e-mail.</small></p><button type="button" onClick={onRegister}><UserPlus size={16} />Criar conta</button></div>
  </>;
}

function RegisterForm({ onBack, onRegistered, onRegistrationPending, additionalMunicipalities, additionalPlaces }: { onBack: () => void; onRegistered: (user: User) => Promise<void>; onRegistrationPending: (pending: boolean) => void; additionalMunicipalities: ReturnType<typeof catalogMunicipalities>; additionalPlaces: ReturnType<typeof catalogCommercialPlaces> }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [location, setLocation] = useState<BrazilLocation>({ ...EMPTY_BRAZIL_LOCATION });

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const services = getFirebaseServices();
    if (!services) return;
    if (password.length < 8) {
      setMessage("Crie uma senha com pelo menos 8 caracteres.");
      return;
    }
    if (password !== confirmation) {
      setMessage("A confirmação da senha não corresponde.");
      return;
    }
    if (!isValidBrazilLocation(location, additionalMunicipalities)) {
      setMessage("Selecione um estado e um município existente na base oficial do IBGE.");
      return;
    }

    setBusy(true);
    setMessage("");
    onRegistrationPending(true);
    try {
      services.auth.languageCode = "pt-BR";
      await setPersistence(services.auth, browserLocalPersistence);
      const credential = await createUserWithEmailAndPassword(services.auth, email.trim(), password);
      try {
        await saveAccountLocation({ mode: "firebase", profileId: "", uid: credential.user.uid }, location, additionalMunicipalities);
      } catch (error) {
        await deleteUser(credential.user).catch(() => signOut(services.auth));
        throw error;
      }
      onRegistrationPending(false);
      await onRegistered(credential.user);
    } catch (error) {
      setMessage(authErrorMessage(error, "Não foi possível criar a conta agora."));
    } finally {
      onRegistrationPending(false);
      setBusy(false);
    }
  }

  return <div className="auth-register-form">
    <button className="auth-back" type="button" onClick={onBack}><ArrowLeft size={16} />Voltar ao login</button>
    <span className="auth-kicker"><UserPlus size={15} />Nova conta</span>
    <h1>Crie seu acesso individual</h1>
    <p className="auth-intro">A conta será criada imediatamente. Informe sua cidade para personalizar as ofertas e preencher futuros anúncios.</p>
    <form onSubmit={handleSubmit}>
      <label><span>E-mail</span><input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="seuemail@exemplo.com" autoComplete="email" required /></label>
      <label><span>Senha</span><div className="auth-password"><LockKeyhole size={17} /><input type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Mínimo de 8 caracteres" autoComplete="new-password" minLength={8} required /></div></label>
      <label><span>Confirme a senha</span><div className="auth-password"><LockKeyhole size={17} /><input type="password" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} placeholder="Digite novamente" autoComplete="new-password" minLength={8} required /></div></label>
      <LocationFields value={location} onChange={setLocation} idPrefix="account-registration" required disabled={busy} additionalMunicipalities={additionalMunicipalities} additionalPlaces={additionalPlaces} />
      {message && <div className="auth-error" role="alert">{message}</div>}
      <button className="auth-submit" disabled={busy || !email.trim() || !password || !confirmation || !isValidBrazilLocation(location, additionalMunicipalities)}>{busy ? "Criando conta…" : "Criar conta"}</button>
    </form>
  </div>;
}

function ResetPasswordForm({ onBack }: { onBack: () => void }) {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const services = getFirebaseServices();
    if (!services || !email.trim()) return;
    setBusy(true);
    setMessage("");
    try {
      services.auth.languageCode = "pt-BR";
      await sendPasswordResetEmail(services.auth, email.trim(), { url: window.location.origin });
      setSent(true);
    } catch (error) {
      if (errorCode(error) === "auth/invalid-email") setMessage("Informe um endereço de e-mail válido.");
      else setSent(true);
    } finally {
      setBusy(false);
    }
  }

  return <>
    <button className="auth-back" type="button" onClick={onBack}><ArrowLeft size={16} />Voltar ao login</button>
    <span className="auth-kicker"><KeyRound size={15} />Recuperação de acesso</span>
    <h1>Redefina sua senha</h1>
    <p className="auth-intro">Informe o e-mail da conta. Se ele estiver cadastrado, você receberá as instruções de redefinição.</p>
    {sent ? <div className="auth-success"><Mail size={24} /><p><strong>Verifique sua caixa de entrada</strong><small>Se a conta existir, o Firebase enviará um link para redefinir a senha. Confira também o spam.</small></p></div> : <form onSubmit={handleSubmit}><label><span>E-mail</span><input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="seuemail@exemplo.com" autoComplete="email" required /></label>{message && <div className="auth-error" role="alert">{message}</div>}<button className="auth-submit" disabled={busy || !email.trim()}>{busy ? "Enviando…" : "Enviar link de redefinição"}</button></form>}
  </>;
}

function AccountPanel({ user, photoUrl, onClose, onOpenMyListings, onOpenFavorites, onOpenProfileSettings, onOpenProfilePhoto }: { user: User; photoUrl?: string; onClose: () => void; onOpenMyListings: () => void; onOpenFavorites: () => void; onOpenProfileSettings: () => void; onOpenProfilePhoto: () => void }) {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const services = getFirebaseServices();
  const providerLabel = user.providerData.some((item) => item.providerId === "google.com") ? "Conta Google" : "E-mail e senha";

  return <>
    <h1>Gerenciar conta</h1>
    <div className="auth-account-summary"><AvatarVisual profile={{ name: user.displayName || "Conta Terra Boa", initials: (user.displayName?.[0] ?? user.email?.[0] ?? "U").toUpperCase(), avatar: accountAvatar(photoUrl || user.photoURL) }} className="account-avatar" eager /><p><strong>{user.displayName || "Conta Terra Boa"}</strong><small>{user.email}</small><em>{providerLabel}</em></p><button className="auth-secondary auth-photo-button" type="button" onClick={onOpenProfilePhoto}><Camera size={16} />Trocar de foto</button></div>
    <div className="auth-gate-actions"><button className="auth-submit" type="button" onClick={onOpenMyListings}><ClipboardList size={16} />Meus anúncios</button><button className="auth-secondary" type="button" onClick={onOpenFavorites}><Heart size={16} />Favoritos</button><button className="auth-secondary" type="button" onClick={onOpenProfileSettings}>Editar dados da conta</button><button className="auth-secondary" type="button" onClick={() => services && void signOut(services.auth)}><LogOut size={16} />Sair da conta</button><button className="auth-secondary" type="button" onClick={onClose}>Continuar navegando</button><button className="auth-danger-link" type="button" onClick={() => setDeleteOpen(true)}><Trash2 size={15} />Excluir esta conta</button></div>
    {deleteOpen && <DeleteAccountPanel user={user} onCancel={() => setDeleteOpen(false)} />}
  </>;
}

function DeleteAccountPanel({ user, onCancel }: { user: User; onCancel: () => void }) {
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const usesPassword = user.providerData.some((item) => item.providerId === "password");
  const usesGoogle = user.providerData.some((item) => item.providerId === "google.com");

  async function handleDelete(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (confirmation !== "12345" || (usesPassword && (!user.email || !password))) return;
    setBusy(true);
    setMessage("");
    try {
      if (usesPassword && user.email) {
        await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, password));
      } else if (usesGoogle) {
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({ prompt: "select_account" });
        await reauthenticateWithPopup(user, provider);
      }
      const services = getFirebaseServices();
      if (!services) throw new Error("firebase-unavailable");
      await user.getIdToken(true);
      const deleteAccount = httpsCallable<{ confirmation: string }, { deleted: boolean }>(services.functions, "deleteTerraBoaAccount");
      await deleteAccount({ confirmation: "12345" });
      await signOut(services.auth).catch(() => undefined);
    } catch (error) {
      setMessage(authErrorMessage(error, "Não foi possível excluir a conta. Confirme o acesso e tente novamente."));
      setBusy(false);
    }
  }

  return <div className="auth-delete-panel"><div><Trash2 size={20} /><p><strong>Excluir conta permanentemente</strong><small>Os dados da conta, publicações, respostas, favoritos e arquivos serão removidos. Apenas registros administrativos imutáveis e as informações da exclusão serão preservados para auditoria.</small></p></div><form onSubmit={handleDelete}>{usesPassword && <label><span>Confirme sua senha</span><div className="auth-password"><LockKeyhole size={17} /><input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" required /></div></label>}{usesGoogle && !usesPassword && <div className="auth-google-delete"><span className="google-mark">G</span><p><strong>Confirmação pelo Google</strong><small>Uma janela do Google será aberta antes da exclusão.</small></p></div>}<label><span>Digite os números de 1 a 5: 12345</span><input inputMode="numeric" pattern="12345" maxLength={5} value={confirmation} onChange={(event) => setConfirmation(event.target.value.replace(/\D/g, "").slice(0, 5))} autoComplete="off" required /></label>{message && <div className="auth-error" role="alert">{message}</div>}<div className="auth-delete-actions"><button type="button" onClick={onCancel}>Cancelar</button><button className="danger" disabled={busy || (usesPassword && !password) || confirmation !== "12345"}>{busy ? "Excluindo…" : "Excluir definitivamente"}</button></div></form></div>;
}

function errorCode(error: unknown) {
  return error instanceof FirebaseError ? error.code : "";
}

function authErrorMessage(error: unknown, fallback: string) {
  switch (errorCode(error)) {
    case "auth/email-already-in-use": return "Já existe uma conta cadastrada com este e-mail.";
    case "auth/invalid-email": return "Informe um endereço de e-mail válido.";
    case "auth/invalid-credential":
    case "auth/wrong-password": return "E-mail ou senha inválidos.";
    case "auth/user-disabled": return "Esta conta está bloqueada. Entre em contato com o suporte Terra Boa.";
    case "auth/weak-password": return "A senha não atende aos requisitos de segurança configurados.";
    case "auth/too-many-requests": return "Muitas tentativas foram realizadas. Aguarde um pouco e tente novamente.";
    case "auth/network-request-failed": return "Não foi possível conectar ao serviço de autenticação.";
    case "auth/requires-recent-login": return "Entre novamente antes de realizar esta operação.";
    case "auth/popup-closed-by-user": return "A janela do Google foi fechada antes da conclusão.";
    case "auth/popup-blocked": return "O navegador bloqueou a janela do Google. Permita pop-ups e tente novamente.";
    case "auth/unauthorized-domain": return "Este domínio ainda não está autorizado no Firebase Authentication.";
    case "auth/operation-not-allowed": return "O provedor de acesso ainda não foi habilitado no Firebase.";
    case "auth/account-exists-with-different-credential": return "Este e-mail já está vinculado a outro método de acesso.";
    default: return fallback;
  }
}
